"use client";

import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { LogOut, User2, Settings, ChevronDown, Moon, Menu, LayoutDashboard, Monitor, Users, BarChart3, Wrench, Shield } from "lucide-react";
import { Button } from "./ui/button";
import { Switch } from "./ui/switch";
import { type Session } from "@/types/auth";
import Link from "next/link";
import { SignInButton } from "./Buttons";
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useTheme } from "next-themes";
import { useEffect, useState } from "react";
import { userIsAdmin, userIsUniversalAdmin } from "@/utils/accessControl";
import { Label } from "./ui/label";

export const UserInfo = ({ session }: { session: Session | null }) => {
    const [mounted, setMounted] = useState(false)
    const { theme, setTheme } = useTheme()

    // useEffect only runs on the client, so now we can safely show the UI
    useEffect(() => {
        setMounted(true)
    }, [])

    useEffect(() => {
        const media = window.matchMedia("(prefers-color-scheme: dark)");
        const onChange = (e: MediaQueryListEvent) => {
            setTheme(e.matches ? "dark" : "light");
        };
        media.addEventListener("change", onChange);
        return () => media.removeEventListener("change", onChange);
    }, [setTheme]);

    if (!session) return <SignInButton className="" size="lg" />

    return (
        <div className="flex gap-2 items-center">
            <DropdownMenu>
                <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="h-12 text-foreground flex items-center gap-2 p-2">
                        <div className="text-sm hidden xl:block">
                            {session ? session.user.email : ""}
                        </div>
                        <Avatar className="h-8 w-8">
                            <AvatarImage src={session.user.image ?? ''} />
                            <AvatarFallback>
                                <User2 size="16" />
                            </AvatarFallback>
                        </Avatar>
                        <ChevronDown size="16" className="hidden xl:block" />
                        <Menu size="16" className="xl:hidden" />
                    </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" avoidCollisions className="mt-1 rounded-lg">
                    {/* Navigation items - only visible on mobile */}
                    <div className="xl:hidden">
                        <DropdownMenuItem asChild>
                            <Link href="/dashboard" className="flex items-center gap-2 cursor-pointer">
                                <LayoutDashboard size="16" />
                                Dashboard
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link href="/devicelist" className="flex items-center gap-2 cursor-pointer">
                                <Monitor size="16" />
                                Devices
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link href="/grouplist" className="flex items-center gap-2 cursor-pointer">
                                <Users size="16" />
                                Groups
                            </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                            <Link href="/reports" className="flex items-center gap-2 cursor-pointer">
                                <BarChart3 size="16" />
                                Reports
                            </Link>
                        </DropdownMenuItem>
                        {userIsUniversalAdmin(session) ?
                            <DropdownMenuItem asChild>
                                <Link href="/deviceconfigurations" className="flex items-center gap-2 cursor-pointer">
                                    <Wrench size="16" />
                                    Device configurations
                                </Link>
                            </DropdownMenuItem>
                            : null}
                        {userIsAdmin(session) ?
                            <DropdownMenuItem asChild>
                                <Link href="/admin" className="flex items-center gap-2 cursor-pointer">
                                    <Shield size="16" />
                                    Admin
                                </Link>
                            </DropdownMenuItem> : null}
                    </div>
                    
                    {/* User-specific items - always visible */}
                    <DropdownMenuItem asChild>
                        <Link href="/user/configuration" className="flex items-center gap-2 cursor-pointer">
                            <Settings size="16" />
                            User configuration
                        </Link>
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {mounted && (
                        <div className="px-2 py-1.5 flex items-center justify-between">
                            <Label className="flex items-center gap-2 cursor-pointer" htmlFor="dark-mode-toggle">
                                <Moon size="16" />
                                <span className="text-sm">Dark mode</span>
                            </Label>
                            <Switch
                                id="dark-mode-toggle"
                                checked={theme === 'dark'}
                                onCheckedChange={(checked) => setTheme(checked ? 'dark' : 'light')}
                            />
                        </div>
                    )}
                    <DropdownMenuSeparator />
                    <div className="p-2">
                        <Button asChild>
                            <Link href="/auth/signout">
                                <LogOut size="16" />
                                <span>
                                    Sign out
                                </span>
                            </Link>
                        </Button>
                    </div>
                </DropdownMenuContent>
            </DropdownMenu>
        </div>
    )
}